import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  ICONS,
  Button,
  Typography,
  TYPOGRAPHY_SIZE,
  PLATFORM_THEME_COLORS,
  DeleteModal,
  ButtonsWrapper,
} from '@thinknet-gmbh/platform-common-ui';

import {
  PERMISSION_ACTIONS,
  PLATFORM_PERMISSIONS,
  ProcessTaskTypesEnum,
} from '@thinknet-gmbh/platform-common';

import { useGetProcessTasksForTable } from 'src/hooks/process-tasks/get-process-tasks.hook';
import { useDeleteProcessTask } from 'src/hooks/process-tasks/delete-process-task.hook';

import { getFiltersCount } from 'src/common/helpers/common.helpers';
import PermissionCheck from 'src/components/permission-check';
import { DrawerComponent } from 'src/components/custom-drawer';

import { ChangeTask } from './components/change-task';
import { TasksListTable } from './components/tasks-list-table';
import { TasksTableFilter } from './components/tasks-table-filter'

import { TASKS_LIST_MODALS } from './tasks-list.constants';

import { ITaskTableItem } from './components/tasks-list-table/tasks-list-table.interfaces';

import { HeaderContainer } from './tasks-list.styles';

import { ITasksFilter } from '../../task-manager.interfaces';

const TaskList = () => {
  const [filters, setFilters] = useState<ITasksFilter>({});
  const { t } = useTranslation(['processBuilder', 'common']);
  const [activeModal, setActiveModal] = useState<string | null>(null);
  const [task, setTask] = useState<ITaskTableItem | null>(null);

  const { processTasks, paginationConfig, loading } = useGetProcessTasksForTable(filters);

  const handleCreate = () => {
    setActiveModal(TASKS_LIST_MODALS.create);
  };

  const closeModal = () => {
    setActiveModal(null);
    setTask(null);
  };

  const deleteProcessTask = useDeleteProcessTask({
    onSuccess: closeModal,
  });

  const deleteTask = () => {
    deleteProcessTask({
      variables: { taskId: task?._id },
    });
    setTask(null);
    closeModal();
  };

  const onSubmitDelete = () => {
    deleteTask();
  };

  const isCreate = activeModal === TASKS_LIST_MODALS.create;
  const isEdit = activeModal === TASKS_LIST_MODALS.edit;

  const isProcessesTask = task?.type === ProcessTaskTypesEnum.PROCESS;

  return (
    <>
      <DrawerComponent
        title={t(
          `processBuilder:tasks.labels.${
            isCreate ? 'addNewTask' : isProcessesTask ? 'editProcessTask' : 'editTask'
          }`
        )}
        onClose={closeModal}
        visible={isEdit || isCreate}
        destroyOnClose
      >
        <ChangeTask onClose={closeModal} id={task?._id} />
      </DrawerComponent>
      <TasksTableFilter
        isOpened={activeModal === TASKS_LIST_MODALS.tableFilter}
        onClose={closeModal}
        filters={filters}
        setFilters={setFilters}
      />
      <DeleteModal
        translate={t}
        isOpened={activeModal === TASKS_LIST_MODALS.delete}
        title={t('processBuilder:tasks.deleteModal.title')}
        subTitle={t('processBuilder:tasks.deleteModal.subtitle', {
          taskName: task?.title,
        })}
        onClose={closeModal}
        onSubmit={onSubmitDelete}
      />
      <HeaderContainer>
        <Typography weight="bold" size={TYPOGRAPHY_SIZE.medium}>
          {t('processBuilder:labels.tasks')}
        </Typography>
        <ButtonsWrapper>
          <Button
            type="primary"
            ghost
            icon={ICONS.filterAlt}
            iconColor={PLATFORM_THEME_COLORS.primary.blue}
            onClick={() => setActiveModal(TASKS_LIST_MODALS.tableFilter)}
          >
            {`${t('common:buttons.filterBy')} ${getFiltersCount(filters)}`}
          </Button>
          <PermissionCheck
            permission={PLATFORM_PERMISSIONS.taskManagement}
            action={PERMISSION_ACTIONS.create}
          >
            <Button
              type="primary"
              icon={ICONS.plus}
              iconColor={PLATFORM_THEME_COLORS.primary.white}
              onClick={handleCreate}
            >
              {t('processBuilder:tasks.buttons.createNewTask')}
            </Button>
          </PermissionCheck>
        </ButtonsWrapper>
      </HeaderContainer>
      <TasksListTable
        paginationConfig={paginationConfig}
        loading={loading}
        dataSource={processTasks}
        handleModals={(modal, record) => {
          setActiveModal(modal);
          setTask(record);
        }}
      />
    </>
  );
};

export default TaskList;
